import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../pages/NavBar/NavBar";
import InputText from "../common/InputText";
import Button from "../common/Button";
import Dropdown from "../../utils/components/Dropdown";
import ReactQuill from "react-quill";
import { getIdMeBaseApiUrl } from "../../utils/UrlConstants";
import { toast } from "react-toastify";
import FooterData from "../pages/NavBar/FooterData";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { useMsal } from '@azure/msal-react';
import { base64Utils } from "../../utils/base64Utils";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { useIdentityAppService } from "../../services/IdentityServices";
import SearchableDropdown from "../../utils/components/SearchableDropdown";

// Interface for registration form data
interface RegistrationFormData {
    userName: string;
    jobTitle: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    organizationType: string;
    notes: string;
    addressLine1: string;
    addressLine2: string;
    state: string;
    city: string;
    zip: string;
    country: string;
}

// Interface for form errors
interface FormErrors {
    [key: string]: string;
}

// Interface for organization domain (matches backend DomainDto)
interface OrganizationDomain {
    orgDomainId: number;
    domainName: string;
    isPrimary: boolean;
    isVerified: boolean;
    operationalFlag: string;
    operationalStatus: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
}

// Interface for organization address (matches backend AddressDto)
interface OrganizationAddress {
    orgAddressId: number;
    addressType?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    zip: string;
    country?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    isPrimary: boolean;
    operationalFlag: string;
    operationalStatus: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
}

// Interface for organization from API
interface OrganizationOption {
    orgCd: string;
    orgName: string;
    orgType: {
        id: number;
        codeType: string;
        code: string;
        value: string;
        createdDate: string;
        modifiedDate: string;
        createdBy: string;
        modifiedBy: string;
        description: string;
    };
    stateIssuedId?: string;
    domains?: OrganizationDomain[];
    addresses?: OrganizationAddress[];
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T & { cancel: () => void } {
    let timeoutId: NodeJS.Timeout;

    const debouncedFunction = ((...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    }) as T & { cancel: () => void };

    debouncedFunction.cancel = () => {
        clearTimeout(timeoutId);
    };

    return debouncedFunction;
}

const SelfRegistration: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { accounts } = useMsal();

    // Page state - 'userType', 'organizationDetails', or 'registration'
    const [currentStep, setCurrentStep] = useState<'userType' | 'organizationDetails' | 'registration'>('userType');

    // User type state
    const [selectedUserType, setSelectedUserType] = useState<string>('');

    // Interface for address in organization details
    interface OrgAddress {
        orgAddressId?: number; // Optional - only present for existing organization addresses
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    }

    // Organization details state (for Trading Partner)
    const [organizationDetails, setOrganizationDetails] = useState({
        organizationName: '',
        organizationType: '',
        stateIssuedId: '',
        notes: '',
        domains: [] as string[],
        addresses: [] as OrgAddress[]
    });

    // Domain input state
    const [domainInput, setDomainInput] = useState('');
    const [domainError, setDomainError] = useState('');

    // Address input state
    const [addressInput, setAddressInput] = useState<OrgAddress>({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    });
    const [addressError, setAddressError] = useState('');
    const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);

    // Selected organization address state (for linking to user)
    const [selectedOrgAddressIndex, setSelectedOrgAddressIndex] = useState<number | null>(null);

    // State for "use same as organization address" checkbox
    const [useSameAsOrgAddress, setUseSameAsOrgAddress] = useState<boolean>(false);

    // Organization search state
    const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
    const [selectedOrgCd, setSelectedOrgCd] = useState<number | null>(null);
    const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
    const [orgSearchLoading, setOrgSearchLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState<RegistrationFormData>({
        userName: '',
        jobTitle: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organizationType: '',
        notes: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        city: '',
        zip: '',
        country: '',
    });

    // Form errors state
    const [errors, setErrors] = useState<FormErrors>({});

    // Username auto-generation state
    const [isUserNameModified, setIsUserNameModified] = useState(false);
    const [suggestedUsername, setSuggestedUsername] = useState<string>('');

    // Address suggestion state
    const [suggestedAddress, setSuggestedAddress] = useState<{
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    }>({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    });

    // State codes from backend
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);

    // Country codes from backend
    const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([]);

    // User type options - will be populated from API when in user creation mode
    const [userTypes, setUserTypes] = useState<{ value: string; label: string }[]>([]);

    // Organization type options - will be populated from API when in user creation mode
    const [organizationTypes, setOrganizationTypes] = useState<{ value: string; label: string }[]>([]);

    // Function to fetch next user number for a given username root (e.g., "jdoe")
    // Uses backend endpoint to compute efficiently; if none exists, starts at 001
    const fetchNextUserNumber = async (userRoot: string): Promise<number> => {
        if (!userRoot) return 1;

        try {
            const response = await useIdentityAppService().getNextUserNumber(userRoot);

            if (response.ok) {
                const data = await response.json();
                if (data && data.success && typeof data.nextUserNumber === 'number') {
                    return data.nextUserNumber;
                }
            }

            // Fallback to 001 if API fails
            console.warn('Failed to fetch next user number for root, using fallback');
            return 1;
        } catch (error) {
            console.error('Error determining next user number:', error);
            return 1; // Fallback
        }
    };

    // Function to generate suggested username
    // Format: First letter of first name (uppercase) + Last name (first char uppercase) + hyphen + number
    // Example: First Name: "test", Last Name: "user" → "TUser-001"
    const generateSuggestedUsername = async (firstName: string, lastName: string): Promise<string> => {
        if (!firstName.trim() || !lastName.trim()) {
            return '';
        }

        // Get first letter of first name and convert to uppercase
        const firstInitial = firstName.trim().charAt(0).toUpperCase();

        // Get last name: first character uppercase, rest lowercase, remove spaces and special characters
        const cleanLastName = lastName.trim().replace(/[^a-zA-Z0-9]/g, '');
        const formattedLastName = cleanLastName.charAt(0).toUpperCase() + cleanLastName.slice(1).toLowerCase();

        // Determine next user number based on existing usernames with same root
        const usernameRoot = `${firstInitial}${formattedLastName}`;
        const nextUserNumber = await fetchNextUserNumber(usernameRoot);

        // Format the number with leading zeros (3 digits)
        const formattedNumber = nextUserNumber.toString().padStart(3, '0');

        // Combine with hyphen separator: TUser-001
        return `${usernameRoot}-${formattedNumber}`;
    };

    // Effect to fetch state codes from backend
    useEffect(() => {
        const fetchStateCodes = async () => {
            try {
                console.log('Fetching state codes from:', `${getIdMeBaseApiUrl()}/code-lookup/by-type/State%20Dropdown`);
                const response = await useIdentityAppService().getCodeLookupByType();

                if (response.ok) {
                    const data = await response.json();
                    console.log('State codes response:', data);
                    if (data.success && data.codes) {
                        const states = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        console.log('Processed state options:', states);
                        setStateOptions(states);
                    } else {
                        console.warn('No state codes found in response');
                    }
                } else {
                    console.error('Failed to fetch state codes, status:', response.status);
                    const errorText = await response.text();
                    console.error('Error response:', errorText);
                }
            } catch (error) {
                console.error('Error fetching state codes:', error);
            }
        };

        fetchStateCodes();
    }, []);

    // Effect to handle when selected organization address changes
    useEffect(() => {
        // If the selected org address changes while the checkbox is checked, update the user address
        if (useSameAsOrgAddress && selectedOrgAddressIndex !== null) {
            const orgAddress = organizationDetails.addresses[selectedOrgAddressIndex];
            console.log('[useEffect] Organization address changed, updating form data');
            console.log('[useEffect] Selected org address:', orgAddress);

            if (orgAddress) {
                // Trim and ensure state and country values are properly formatted as strings
                const stateValue = orgAddress.state ? String(orgAddress.state).trim() : '';
                const countryValue = orgAddress.country ? String(orgAddress.country).trim() : '';

                console.log('[useEffect] State value:', stateValue);
                console.log('[useEffect] Country value:', countryValue);

                setFormData(prev => ({
                    ...prev,
                    addressLine1: orgAddress.addressLine1 || '',
                    addressLine2: orgAddress.addressLine2 || '',
                    city: orgAddress.city || '',
                    state: stateValue,
                    zip: orgAddress.zip || '',
                    country: countryValue
                }));
            }
        }
    }, [selectedOrgAddressIndex, useSameAsOrgAddress, organizationDetails.addresses]);

    // Effect to fetch country codes from backend
    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                console.log('Fetching country codes from:', `${getIdMeBaseApiUrl()}/code-lookup/by-type/Country%20Dropdown`);
                const response = await useIdentityAppService().getCodeLookupByTypeCountryDropdown();

                if (response.ok) {
                    const data = await response.json();
                    console.log('Country codes response:', data);
                    if (data.success && data.codes) {
                        const countries = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        console.log('Processed country options:', countries);
                        setCountryOptions(countries);

                        // Default to USA if country is not already set
                        setFormData((prev: any) => {
                            if (!prev.country || prev.country.trim() === '') {
                                const usaCountry = countries.find((c: any) => c.value === 'US' || c.value === 'USA');
                                return {
                                    ...prev,
                                    country: usaCountry ? usaCountry.value : ''
                                };
                            }
                            return prev;
                        });
                    } else {
                        console.warn('No country codes found in response');
                    }
                } else {
                    console.error('Failed to fetch country codes, status:', response.status);
                    const errorText = await response.text();
                    console.error('Error response:', errorText);
                }
            } catch (error) {
                console.error('Error fetching country codes:', error);
            }
        };

        fetchCountryCodes();
    }, []);

    // Effect to auto-generate username when first name or last name changes
    useEffect(() => {
        const updateUsername = async () => {
            if (formData.firstName && formData.lastName) {
                const newSuggestedUsername = await generateSuggestedUsername(formData.firstName, formData.lastName);
                setSuggestedUsername(newSuggestedUsername);

                // Only auto-generate if the user hasn't manually modified the username
                if (!isUserNameModified) {
                    setFormData(prev => ({
                        ...prev,
                        userName: newSuggestedUsername
                    }));
                }
            } else {
                setSuggestedUsername('');
            }
        };

        updateUsername();
    }, [formData.firstName, formData.lastName, isUserNameModified]);

    // Function to search organizations from API
    const searchOrganizations = async (searchTerm: string, fetchAll: boolean = false) => {
        // If fetching all for dropdown, use organization type as filter
        if (fetchAll && organizationDetails.organizationType) {
            try {
                setOrgSearchLoading(true);
                const response = await useIdentityAppService().getOrganizationsByType(organizationDetails.organizationType);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.organizations) {
                        setOrganizationOptions(
                            [...data.organizations].sort((a: any, b: any) =>
                                Number(a.orgCd ?? 0) - Number(b.orgCd ?? 0)
                            )
                        );
                    } else {
                        setOrganizationOptions([]);
                    }
                } else {
                    console.error('Failed to fetch organizations by type:', response.status);
                    setOrganizationOptions([]);
                }
            } catch (error: any) {
                console.error('Error fetching organizations by type:', error);
                setOrganizationOptions([]);
            } finally {
                setOrgSearchLoading(false);
            }
            return;
        }

        // Original search logic for non-user-creation flow
        if (!searchTerm || searchTerm.length < 2) {
            setOrganizationOptions([]);
            setIsOrgDropdownOpen(false);
            return;
        }

        try {
            setOrgSearchLoading(true);
            const response = await useIdentityAppService().searchOrganizations(searchTerm);

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.organizations) {
                    setOrganizationOptions(data.organizations);
                    setIsOrgDropdownOpen(data.organizations.length > 0);
                } else {
                    setOrganizationOptions([]);
                    setIsOrgDropdownOpen(false);
                }
            } else {
                console.error('Failed to search organizations:', response.status);
                setOrganizationOptions([]);
                setIsOrgDropdownOpen(false);
            }
        } catch (error: any) {
            console.error('Error searching organizations:', error);
            setOrganizationOptions([]);
            setIsOrgDropdownOpen(false);
        } finally {
            setOrgSearchLoading(false);
        }
    };

    // Debounced organization search
    const debouncedSearchOrganizations = useMemo(
        () => debounce(searchOrganizations, 300),
        []
    );

    // Determine if this is user creation from admin or self-registration
    const isUserCreation = location.state?.from === 'admin-user-management';

    // Effect to fetch user types from API for both user creation and self-registration
    useEffect(() => {
        const fetchUserTypes = async () => {
            try {
                // Always fetch from API to get proper code values (10, 20, 30)
                const response = await useIdentityAppService().getUserTypes();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.userTypes) {
                        const formattedUserTypes = data.userTypes
                            .map((type: any) => ({
                                value: type.value, // This is the code (10, 20, 30)
                                label: type.label  // This is the display value (Citizen, Member, etc.)
                            }))
                            .filter((type: any) => type.label.toLowerCase() !== 'internal'); // Filter out Internal option
                        setUserTypes(formattedUserTypes);
                    }
                } else {
                    console.error('Failed to fetch user types');
                    // Fallback to hardcoded values if API fails
                    setUserTypes([
                        { value: '10', label: 'Citizen' },
                        { value: '20', label: 'Member' },
                        { value: '30', label: 'Trading Partner' }
                    ]);
                }
            } catch (error) {
                console.error('Error fetching user types:', error);
                // Fallback to hardcoded code values if API fails
                setUserTypes([
                    { value: '10', label: 'Citizen' },
                    { value: '20', label: 'Member' },
                    { value: '30', label: 'Trading Partner' }
                ]);
            }
        };

        fetchUserTypes();
    }, []);

    // Effect to fetch organization types from API (both user creation and self-registration now need API values)
    useEffect(() => {
        const fetchOrganizationTypes = async () => {
            try {
                // Fetch from API for both user creation and self-registration
                const response = await useIdentityAppService().getOrganizationTypes();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.organizationTypes) {
                        const formattedOrgTypes = [...data.organizationTypes]
                            .sort((a: any, b: any) => Number(a.code ?? 0) - Number(b.code ?? 0))
                            .map((type: any) => ({
                                value: type.value, // This is the display value
                                label: type.value  // Use value for both since API doesn't have label property
                            }));
                        setOrganizationTypes(formattedOrgTypes);
                    }
                } else {
                    console.error('Failed to fetch organization types');
                    // Fallback to hardcoded values if API fails
                    setOrganizationTypes([
                        { value: 'Managed Care Organizations', label: 'Managed Care Organizations' },
                        { value: 'HealthCare Service Providers', label: 'HealthCare Service Providers' },
                        { value: 'Fiscal Intermediaries /Agents', label: 'Fiscal Intermediaries /Agents' },
                        { value: 'Vendors', label: 'Vendors' },
                        { value: 'Not for Profit Organizations', label: 'Not for Profit Organizations' },
                        { value: 'Consultants', label: 'Consultants' },
                        { value: 'Others', label: 'Others' }
                    ]);
                }
            } catch (error) {
                console.error('Error fetching organization types:', error);
                // Fallback to hardcoded values
                setOrganizationTypes([
                    { value: 'Managed Care Organizations', label: 'Managed Care Organizations' },
                    { value: 'HealthCare Service Providers', label: 'HealthCare Service Providers' },
                    { value: 'Fiscal Intermediaries /Agents', label: 'Fiscal Intermediaries /Agents' },
                    { value: 'Vendors', label: 'Vendors' },
                    { value: 'Not for Profit Organizations', label: 'Not for Profit Organizations' },
                    { value: 'Consultants', label: 'Consultants' },
                    { value: 'Others', label: 'Others' }
                ]);
            }
        };

        fetchOrganizationTypes();
    }, []);

    // Effect to fetch organizations when organization type changes (for self-registration dropdown)
    // Only fetch if organizationName is empty (not already selected)
    useEffect(() => {
        if (!isUserCreation && organizationDetails.organizationType && !organizationDetails.organizationName) {
            searchOrganizations('', true);
        }
    }, [organizationDetails.organizationType, isUserCreation]);

    // Handle user type dropdown change
    const handleUserTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedUserType(event.target.value);
    };

    // Handle next button click
    const handleNext = () => {
        // Check for both old values (self-registration) and new codes (user creation)
        if (selectedUserType === 'citizen' || selectedUserType === '10' ||
            selectedUserType === 'member' || selectedUserType === '20') {
            setCurrentStep('registration');
        } else if (selectedUserType === 'trading_partner' || selectedUserType === '30') {
            setCurrentStep('organizationDetails');
        }
    };

    // Handle next button click from organization details
    const handleOrganizationNext = () => {
        setCurrentStep('registration');
    };

    // Handle back button click
    const handleBack = () => {
        if (currentStep === 'organizationDetails') {
            setCurrentStep('userType');
            setSelectedOrgAddressIndex(null); // Clear selected address when going back
            setUseSameAsOrgAddress(false); // Reset checkbox
        } else if (currentStep === 'registration') {
            // Check for both old values and new codes for trading partner
            if (selectedUserType === 'trading_partner' || selectedUserType === '30') {
                setCurrentStep('organizationDetails');
            } else {
                setCurrentStep('userType');
                setSelectedOrgAddressIndex(null); // Clear selected address when going back
                setUseSameAsOrgAddress(false); // Reset checkbox
            }
        }
    };

    // Handle organization details input changes with search
    const handleOrganizationInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (isUserCreation && name === 'stateIssuedId') return;
        setOrganizationDetails(prev => ({
            ...prev,
            [name]: value
        }));

        // If it's the organization name field, trigger search
        if (name === 'organizationName') {
            setSelectedOrgCd(null);
            if (value.trim().length >= 2) {
                debouncedSearchOrganizations(value.trim());
            } else {
                setOrganizationOptions([]);
                setIsOrgDropdownOpen(false);
            }
        }
    };

    // Handle organization selection from dropdown/search
    const handleOrganizationSelection = (organization: OrganizationOption, clearOptions: boolean = true) => {
        setSelectedOrgCd(Number(organization.orgCd));
        // Extract domains from the selected organization
        const existingDomains: string[] = [];
        if (organization.domains && organization.domains.length > 0) {
            // Filter for active domains and extract domain names
            organization.domains.forEach(domain => {
                if (domain.operationalStatus === 'Active' && domain.domainName) {
                    existingDomains.push(domain.domainName.toLowerCase());
                }
            });
        }

        // Extract addresses from the selected organization
        const existingAddresses: OrgAddress[] = [];
        if (organization.addresses && organization.addresses.length > 0) {
            // Filter for active addresses and transform to OrgAddress format
            organization.addresses.forEach(address => {
                if (address.operationalStatus === 'Active') {
                    existingAddresses.push({
                        orgAddressId: address.orgAddressId, // Include the ID for linking to user
                        addressLine1: address.addressLine1 || '',
                        addressLine2: address.addressLine2 || '',
                        city: address.city || '',
                        state: address.state?.code || '',
                        zip: address.zip || '',
                        country: address.country?.code || ''
                    });
                }
            });
        }

        setOrganizationDetails(prev => ({
            ...prev,
            organizationName: organization.orgName,
            organizationType: organization?.orgType?.value, // Extract the value from the orgType object
            stateIssuedId: organization?.stateIssuedId || '',
            domains: existingDomains, // Populate domains from the selected organization
            addresses: existingAddresses // Populate addresses from the selected organization
        }));
        setIsOrgDropdownOpen(false);

        // Only clear options for user creation (search autocomplete), not for self-registration (dropdown)
        if (clearOptions) {
            setOrganizationOptions([]);
        }

        // Validate existing email against the organization's domains
        if (existingDomains.length > 0 && formData.email) {
            validateEmailAgainstDomains(formData.email, existingDomains);
        }
    };

    // Handle organization details dropdown change
    const handleOrganizationDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;

        // If organization type is changing, clear the organization name and state issued ID
        if (name === 'organizationType') {
            setSelectedOrgCd(null);
            setOrganizationDetails(prev => ({
                ...prev,
                organizationType: value,
                organizationName: '',
                stateIssuedId: ''
            }));
            setOrganizationOptions([]); // Clear existing options
            setSelectedOrgAddressIndex(null); // Clear selected address when organization changes
            setUseSameAsOrgAddress(false); // Reset checkbox
        } else {
            setOrganizationDetails(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle input changes
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // Special handling for username field
        if (name === 'userName') {
            setIsUserNameModified(true); // Mark username as manually modified
        }

        // If user manually edits any address field, uncheck the "use same as org address" checkbox
        const addressFields = ['addressLine1', 'addressLine2', 'city', 'state', 'zip', 'country'];
        if (addressFields.includes(name) && useSameAsOrgAddress) {
            setUseSameAsOrgAddress(false);
        }

        // Special handling for phone field - format as (XXX)-XXX-XXXX
        if (name === 'phone') {
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prev => ({
                ...prev,
                phone: formattedPhone
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Real-time email domain validation for trading partners with domains
        if (name === 'email' && value.includes('@') &&
            (selectedUserType === 'trading_partner' || selectedUserType === '30') &&
            organizationDetails.domains.length > 0) {

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(value)) {
                const emailDomain = value.split('@')[1]?.toLowerCase();
                const matchesDomain = organizationDetails.domains.some(domain =>
                    emailDomain === domain.toLowerCase()
                );

                if (!matchesDomain) {
                    setErrors(prev => ({
                        ...prev,
                        email: `Email domain must match one of the organization domains: ${organizationDetails.domains.join(', ')}`
                    }));
                }
            }
        }
    };

    // Handle username regeneration button click
    const handleRegenerateUsername = async () => {
        if (formData.firstName && formData.lastName) {
            const newSuggestedUsername = await generateSuggestedUsername(formData.firstName, formData.lastName);
            setSuggestedUsername(newSuggestedUsername);
            setFormData(prev => ({
                ...prev,
                userName: newSuggestedUsername
            }));
            setIsUserNameModified(false); // Reset the modification flag
        }
    };

    // Handle "use same as organization address" checkbox
    const handleUseSameAsOrgAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setUseSameAsOrgAddress(isChecked);

        if (isChecked && selectedOrgAddressIndex !== null) {
            // Copy the selected organization address to user's address fields
            const orgAddress = organizationDetails.addresses[selectedOrgAddressIndex];
            console.log('Selected organization address:', orgAddress);
            console.log('Selected org address index:', selectedOrgAddressIndex);

            if (orgAddress) {
                // Trim and ensure state and country values are properly formatted as strings
                const stateValue = orgAddress.state ? String(orgAddress.state).trim() : '';
                const countryValue = orgAddress.country ? String(orgAddress.country).trim() : '';

                console.log('State value being set:', stateValue);
                console.log('Country value being set:', countryValue);
                console.log('State options available:', stateOptions);
                console.log('Country options available:', countryOptions);

                setFormData(prev => {
                    const newFormData = {
                        ...prev,
                        addressLine1: orgAddress.addressLine1 || '',
                        addressLine2: orgAddress.addressLine2 || '',
                        city: orgAddress.city || '',
                        state: stateValue,
                        zip: orgAddress.zip || '',
                        country: countryValue
                    };
                    console.log('New form data after update:', newFormData);
                    return newFormData;
                });
            } else {
                console.log('No organization address found at index:', selectedOrgAddressIndex);
            }
        } else if (!isChecked) {
            // Clear the address fields when unchecked
            setFormData(prev => ({
                ...prev,
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: '',
                country: ''
            }));
        }
    };

    // Handle address suggestion - for now, returns the same address
    // This will be replaced with API call later
    const handleSuggestAddress = () => {
        // For now, just use the current address as the "suggested" address
        // Later, this will call an API to validate/suggest the address
        const currentAddress = {
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            country: formData.country
        };

        setSuggestedAddress(currentAddress);

        // Apply the suggested address to the form
        setFormData(prev => ({
            ...prev,
            ...currentAddress
        }));
    };

    // Handle dropdown change
    const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;

        // If user manually changes state or country dropdown, uncheck the "use same as org address" checkbox
        const addressFields = ['state', 'country'];
        if (addressFields.includes(name) && useSameAsOrgAddress) {
            setUseSameAsOrgAddress(false);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Check if username already exists
    const checkUsernameExists = async (username: string): Promise<boolean> => {
        if (!username?.trim()) return false;
        try {
            const response = await useIdentityAppService().getCheckUserByUsername(username);
            if (response.ok) {
                const data = await response.json();
                return !!data.exists;
            }
            console.warn('Username check failed with status:', response.status);
            return false;
        } catch (e) {
            console.error('Error checking username existence:', e);
            return false;
        }
    };

    // Check if email already exists
    const checkEmailExists = async (email: string): Promise<boolean> => {
        try {
            const encodedEmail = btoa(email);
            const response = await useIdentityAppService().getCheckUserByEmail(encodedEmail);

            if (response.ok) {
                const data = await response.json();
                return data.exists;
            } else {
                console.error('Error checking email existence:', response.status);
                return false;
            }
        } catch (error) {
            console.error('Error checking email existence:', error);
            return false;
        }
    };

    // Get the referring page from location state or use a fallback
    const getReferringPage = () => {
        // Check if we have state indicating where we came from
        /* if (location.state?.from) {
             return location.state.from;
         }
 
         // Check if we came from admin dashboard based on search params
         const searchParams = new URLSearchParams(location.search);
         if (searchParams.has('firstName') || searchParams.has('lastName') || searchParams.has('email')) {
             return '/admin-dashboard';
         }*/

        // Default fallback
        return '/';
    };

    // Handle form submission
    const handleSubmit = async () => {
        // Clear previous errors
        setErrors({});

        // Clear previous errors
        const newErrors: FormErrors = {};

        // Validation
        if (!formData.userName.trim()) {
            newErrors.userName = 'Username is required';
        }
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        }

        const blockedDomain = 'nebraska.gov';
        // Username validation (allow email format or traditional username format)
        if (formData.userName.trim()) {

            const userDomain = formData.userName.split('@')[1]?.toLowerCase();
            if (userDomain === blockedDomain) {
                newErrors.userName = 'Please enter a valid external username or use your AD login for internal user.';
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const usernameRegex = /^[a-zA-Z0-9_-]+$/;

            if (!emailRegex.test(formData.userName) && !usernameRegex.test(formData.userName)) {
                newErrors.userName = 'Username can be an email address or contain only letters, numbers, underscores, and hyphens';
            } else if (!emailRegex.test(formData.userName) && formData.userName.length < 3) {
                newErrors.userName = 'Username must be at least 3 characters long';
            }
        }

        // Email validation
        if (formData.email.trim()) {
            const emailDomain = formData.email.split('@')[1]?.toLowerCase();
            if (emailDomain === blockedDomain) {
                newErrors.email = 'Please enter a valid external email address or use your AD login for internal user.';
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            } else if ((selectedUserType === 'trading_partner' || selectedUserType === '30') &&
                organizationDetails.domains.length > 0) {
                // If user is a trading partner and organization domains are defined,
                // validate that email domain matches one of the organization domains
                const emailDomain = formData.email.split('@')[1]?.toLowerCase();
                const matchesDomain = organizationDetails.domains.some(domain =>
                    emailDomain === domain.toLowerCase()
                );

                if (!matchesDomain) {
                    newErrors.email = `Email domain must match one of the organization domains: ${organizationDetails.domains.join(', ')}`;
                }
            }
        }

        // Phone validation
        if (formData.phone.trim()) {
            // Extract only digits to validate length
            const digits = formData.phone.replace(/\D/g, '');
            if (digits.length !== 10) {
                newErrors.phone = 'Please enter a valid 10-digit phone number';
            }
        }

        // Additional validation for Trading Partner (check both old value and new code)
        if (selectedUserType === 'trading_partner' || selectedUserType === '30') {
            if (!organizationDetails.organizationName.trim()) {
                newErrors.organizationName = 'Organization name is required';
            }
            if (!organizationDetails.organizationType) {
                newErrors.organizationType = 'Organization type is required';
            }
            if (!organizationDetails.stateIssuedId.trim()) {
                newErrors.stateIssuedId = 'State issued ID is required';
            }
        }

        // Address validation - if any address field is filled, all required fields must be filled
        const addressFieldsFilled = formData.addressLine1.trim() || formData.city.trim() ||
            formData.state.trim() || formData.zip.trim() || formData.country.trim();

        if (addressFieldsFilled) {
            if (!formData.addressLine1.trim()) {
                newErrors.addressLine1 = 'Address Line 1 is required when providing address';
            }
            if (!formData.city.trim()) {
                newErrors.city = 'City is required when providing address';
            }
            if (!formData.state.trim()) {
                newErrors.state = 'State is required when providing address';
            }
            if (!formData.zip.trim()) {
                newErrors.zip = 'ZIP code is required when providing address';
            }
            if (!formData.country.trim()) {
                newErrors.country = 'Country is required when providing address';
            }
        }

        // ZIP code format validation
        if (formData.zip.trim()) {
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex.test(formData.zip)) {
                newErrors.zip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
            }
        }

        // If there are validation errors, set them and return
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Check if email already exists
        const emailExists = await checkEmailExists(formData.email);
        if (emailExists) {
            setErrors(prev => ({
                ...prev,
                email: 'User exists with same email address. Sign in instead.'
            }));
            return;
        }

        // Check if username already exists (but allow username to be same as email)
        const usernameExists = await checkUsernameExists(formData.userName);
        if (usernameExists && formData.userName.toLowerCase() !== formData.email.toLowerCase()) {
            setErrors(prev => ({
                ...prev,
                userName: 'Username already taken. Please choose another.'
            }));
            return;
        }

        // Prepare submission data with base64 encoded notes
        const submissionData = {
            userName: formData.userName,
            jobTitle: formData.jobTitle,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone.replace(/\D/g, ''), // Strip formatting, send only digits
            notes: base64Utils.encode(formData.notes),
            userType: selectedUserType,
            // Include address fields
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            state: formData.state,
            city: formData.city,
            zip: formData.zip,
            country: formData.country,
            // Include requestedBy (admin's IDP GUID) if available
            ...(accounts?.[0]?.idTokenClaims?.oid && {
                requestedBy: accounts[0].idTokenClaims.oid
            }),
            // Include organization details for trading partners (check both old value and new code)
            ...((selectedUserType === 'trading_partner' || selectedUserType === '30') && {
                selectedOrgCd,
                organizationName: organizationDetails.organizationName,
                organizationType: organizationDetails.organizationType,
                stateIssuedId: organizationDetails.stateIssuedId,
                organizationNotes: base64Utils.encode(organizationDetails.notes),
                domains: organizationDetails.domains,
                orgAddresses: organizationDetails.addresses,
                // Include the selected organization address ID if one is selected
                selectedOrgAddressId: selectedOrgAddressIndex !== null && organizationDetails.addresses[selectedOrgAddressIndex]?.orgAddressId
                    ? organizationDetails.addresses[selectedOrgAddressIndex].orgAddressId
                    : null
            })
        };

        try {
            console.log('Submitting registration data:', submissionData);

            // Call the API to create the user
            const response = await useIdentityAppService().postCreateUser(JSON.stringify(submissionData));

            // Parse the JSON response
            const responseData = await response.json();

            if (response.status === 201 && responseData.success) {
                // Success - HTTP 201 Created
                toast.success('Account created successfully! Please check your email to proceed further.');
                console.log('User registration successful:', responseData);

                // Get the referring page and redirect there
                const referringPage = getReferringPage();

                // Redirect to the referring page after a short delay to let user see the toast
                // Note: No need to reset form state here since we're navigating away
                setTimeout(() => {
                    navigate(referringPage);
                }, 2000);

            } else if (response.status === 400 || response.status === 500) {
                // Handle API errors with structured error response from backend
                console.error('Registration failed:', response.status, responseData);

                let errorMessage = 'Registration failed. Please try again.';

                // Check for structured error response from backend
                if (responseData && responseData.message) {
                    errorMessage = responseData.message;

                    // Handle specific error types
                    if (responseData.errorType === 'DUPLICATE_EMAIL') {
                        // Set error on email field for duplicate email
                        setErrors({
                            email: errorMessage
                        });
                        return; // Don't set general error if we set field-specific error
                    } else if (responseData.errorType === 'DUPLICATE_USERNAME') {
                        // Set error on username field for duplicate username
                        setErrors({
                            userName: errorMessage
                        });
                        return; // Don't set general error if we set field-specific error
                    } else if (responseData.errorType === 'CREATION_FAILED') {
                        if (responseData.message && responseData.message.includes('Violation of PRIMARY KEY constraint') ||
                            responseData.message && responseData.message.includes('ORGANIZATION_pk')
                        ) {
                            errorMessage = 'An issue creating the organization occurred. Please try again later or contact support.';
                        } else {
                            // Generic creation failure
                            errorMessage = responseData.message || 'Failed to create user account. Please try again.';
                        }
                    } else if (responseData.errorType === 'UNEXPECTED_ERROR') {
                        // Unexpected server error
                        errorMessage = 'An unexpected error occurred. Please try again later or contact support.';
                    }
                }

                setErrors({ general: errorMessage });
            } else {
                // Handle unexpected response status
                console.error('Unexpected response status:', response.status, responseData);
                const errorMessage = responseData?.message || 'An unexpected error occurred. Please try again.';
                setErrors({ general: errorMessage });
            }

        } catch (error) {
            // Handle network or parsing errors
            console.error('Error during registration:', error);

            let errorMessage = 'An unexpected error occurred. Please try again or contact support.';
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (error instanceof SyntaxError) {
                errorMessage = 'Invalid response from server. Please try again or contact support.';
            }

            setErrors({ general: errorMessage });
        }
    };

    // Handle cancel button click
    const handleCancel = () => {
        navigate(-1); // Go back to previous page
    };

    // Validate domain name format
    const isValidDomain = (domain: string): boolean => {
        // Regex for domain validation (e.g., example.com, subdomain.example.com)
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
        return domainRegex.test(domain);
    };

    // Validate email against organization domains
    const validateEmailAgainstDomains = (email: string, domains: string[]) => {
        if (!email || !email.includes('@') || domains.length === 0) {
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
            const emailDomain = email.split('@')[1]?.toLowerCase();
            const matchesDomain = domains.some(domain =>
                emailDomain === domain.toLowerCase()
            );

            if (!matchesDomain) {
                setErrors(prev => ({
                    ...prev,
                    email: `Email domain must match one of the organization domains: ${domains.join(', ')}`
                }));
            } else {
                // Clear email error if domain now matches
                setErrors(prev => ({
                    ...prev,
                    email: ''
                }));
            }
        }
    };

    // Handle adding a domain
    const handleAddDomain = () => {
        if (isUserCreation) return; // Organization details are read-only in admin user creation.
        const trimmedDomain = domainInput.trim().toLowerCase();

        if (!trimmedDomain) {
            setDomainError('Please enter a domain name');
            return;
        }

        if (!isValidDomain(trimmedDomain)) {
            setDomainError('Please enter a valid domain name (e.g., nebraska.gov)');
            return;
        }

        if (organizationDetails.domains.includes(trimmedDomain)) {
            setDomainError('This domain has already been added');
            return;
        }

        const updatedDomains = [...organizationDetails.domains, trimmedDomain];
        setOrganizationDetails(prev => ({
            ...prev,
            domains: updatedDomains
        }));
        setDomainInput('');
        setDomainError('');

        // Validate existing email against new domain list
        validateEmailAgainstDomains(formData.email, updatedDomains);
    };

    // Handle removing a domain
    const handleRemoveDomain = (domainToRemove: string) => {
        if (isUserCreation) return; // Organization details are read-only in admin user creation.
        const updatedDomains = organizationDetails.domains.filter(domain => domain !== domainToRemove);
        setOrganizationDetails(prev => ({
            ...prev,
            domains: updatedDomains
        }));

        // Re-validate email against updated domain list
        validateEmailAgainstDomains(formData.email, updatedDomains);
    };

    // Handle domain input change
    const handleDomainInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDomainInput(event.target.value);
        // Clear error when user starts typing
        if (domainError) {
            setDomainError('');
        }
    };

    // Handle Enter key press in domain input
    const handleDomainInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddDomain();
        }
    };

    // Handle address input change
    const handleAddressInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        setAddressInput(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (addressError) {
            setAddressError('');
        }
    };

    // Validate address fields
    const validateAddress = (address: OrgAddress): string => {
        if (!address.addressLine1.trim()) {
            return 'Address Line 1 is required';
        }
        if (!address.city.trim()) {
            return 'City is required';
        }
        if (!address.state.trim()) {
            return 'State is required';
        }
        if (!address.zip.trim()) {
            return 'ZIP code is required';
        }
        if (!address.country.trim()) {
            return 'Country is required';
        }
        // ZIP code format validation
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!zipRegex.test(address.zip)) {
            return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        }
        return '';
    };

    // Handle adding or updating an address
    const handleSaveAddress = () => {
        if (isUserCreation) return; // Organization details are read-only in admin user creation.
        const validationError = validateAddress(addressInput);
        if (validationError) {
            setAddressError(validationError);
            return;
        }

        if (editingAddressIndex !== null) {
            // Update existing address
            const updatedAddresses = [...organizationDetails.addresses];
            updatedAddresses[editingAddressIndex] = { ...addressInput };
            setOrganizationDetails(prev => ({
                ...prev,
                addresses: updatedAddresses
            }));
            setEditingAddressIndex(null);
        } else {
            // Add new address
            setOrganizationDetails(prev => ({
                ...prev,
                addresses: [...prev.addresses, { ...addressInput }]
            }));
        }

        // Reset address input
        setAddressInput({
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        });
        setAddressError('');
    };

    // Handle editing an address
    const handleEditAddress = (index: number) => {
        if (isUserCreation) return; // Organization details are read-only in admin user creation.
        const addressToEdit = organizationDetails.addresses[index];

        // Trim and ensure state and country values are properly formatted as strings
        const stateValue = addressToEdit.state ? String(addressToEdit.state).trim() : '';
        const countryValue = addressToEdit.country ? String(addressToEdit.country).trim() : '';

        // Ensure state and country values are properly formatted as strings
        const editAddress = {
            orgAddressId: addressToEdit.orgAddressId,
            addressLine1: addressToEdit.addressLine1 || '',
            addressLine2: addressToEdit.addressLine2 || '',
            city: addressToEdit.city || '',
            state: stateValue,
            zip: addressToEdit.zip || '',
            country: countryValue
        };

        setAddressInput(editAddress);
        setEditingAddressIndex(index);
    };

    // Handle removing an address
    const handleRemoveAddress = (index: number) => {
        if (isUserCreation) return; // Organization details are read-only in admin user creation.
        const updatedAddresses = organizationDetails.addresses.filter((_, i) => i !== index);
        setOrganizationDetails(prev => ({
            ...prev,
            addresses: updatedAddresses
        }));
        // If we were editing this address, cancel the edit
        if (editingAddressIndex === index) {
            setEditingAddressIndex(null);
            setAddressInput({
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: '',
                country: ''
            });
        }
    };

    // Handle canceling address edit
    const handleCancelAddressEdit = () => {
        setEditingAddressIndex(null);
        setAddressInput({
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        });
        setAddressError('');
    };

    // Check if organization details form is valid
    const isOrganizationDetailsValid = () => {
        return organizationDetails.organizationName.trim() &&
            organizationDetails.organizationType &&
            organizationDetails.stateIssuedId.trim();
    };

    // Check if form is valid
    const isFormValid = () => {
        return formData.userName.trim() &&
            formData.firstName.trim() &&
            formData.lastName.trim() &&
            formData.email.trim() &&
            formData.phone.trim()
    };

    // Get user type label for display
    const getUserTypeLabel = () => {
        const userType = userTypes.find(type => type.value === selectedUserType);
        return userType ? userType.label : '';
    };

    const titlePrefix = isUserCreation ? 'User Creation' : 'Self Registration';

    return (
        <>
            <NavBar />

            <div className="subheading grid-row">
                <div className="grid-col flex-1">
                    <h3 className="subheading-title" style={{ textAlign: 'center', marginBottom: '20px', marginTop: '40px' }}>
                        {currentStep === 'userType' ? `${titlePrefix} - User Type Selection` :
                            currentStep === 'organizationDetails' ? `${titlePrefix} - Organization Details` : `${titlePrefix} - User Selection`}
                    </h3>
                </div>
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <div style={{
                    padding: '32px',
                    marginBottom: '16px',
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                }}>

                    {/* User Type Selection Step */}
                    {currentStep === 'userType' && (
                        <>
                            <div>
                                <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px', color: '#666' }}>
                                    Please select your user type to begin the registration process.
                                </p>
                            </div>

                            <div className="grid-row" style={{ marginBottom: '30px' }}>
                                <div className="grid-col flex-1" />
                                <div className="grid-col" style={{
                                    width: "50%",
                                    maxWidth: "600px",
                                    margin: "0 auto"
                                }}>
                                    <div className="grid-row">
                                        <div className="grid-col-12">
                                            <Dropdown
                                                choices={userTypes}
                                                emptyChoiceLabel="Select User Type"
                                                mandatoryLabel={true}
                                                name="userType"
                                                label="User Type"
                                                value={selectedUserType}
                                                onChange={handleUserTypeChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-col flex-1" />
                            </div>

                            {/* User Type Description */}
                            {selectedUserType && (
                                <div style={{
                                    textAlign: 'center',
                                    marginTop: '10px',
                                    marginBottom: '20px',
                                    padding: '15px',
                                }}>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#495057',
                                        margin: '0',
                                        fontStyle: 'italic'
                                    }}>
                                        {(selectedUserType === 'citizen' || selectedUserType === '10') &&
                                            'A Nebraska resident who is not currently enrolled in any DHHS programs or services.'
                                        }
                                        {(selectedUserType === 'member' || selectedUserType === '20') &&
                                            'An individual currently enrolled in a DHHS program or service, such as Medicaid, SNAP, or child welfare support.'
                                        }
                                        {(selectedUserType === 'trading_partner' || selectedUserType === '30') &&
                                            'An employee or representative of an organization that contracts or with DHHS to provide services or operational support.'
                                        }
                                    </p>
                                </div>
                            )}

                            {/* Next and Cancel buttons */}
                            <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "20px" }}>
                                <Button
                                    disabled={!selectedUserType}
                                    onClick={handleNext}
                                >
                                    Next
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Organization Details Step (for Trading Partner) */}
                    {currentStep === 'organizationDetails' && (
                        <>
                            <div>
                                <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px', color: '#666' }}>
                                    Please provide your organization details to continue with the registration process.
                                </p>
                            </div>

                            <div className="grid-row" style={{ marginBottom: '20px' }}>
                                <div className="grid-col flex-1" />
                                <div className="grid-col" style={{
                                    width: "50%",
                                    maxWidth: "600px",
                                    margin: "0 auto"
                                }}>

                                    {/* Organization Type */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <Dropdown
                                                choices={organizationTypes}
                                                emptyChoiceLabel="Select Organization Type"
                                                mandatoryLabel={true}
                                                name="organizationType"
                                                label="Organization Type"
                                                value={organizationDetails.organizationType}
                                                onChange={handleOrganizationDropdownChange}
                                            />
                                            {errors.organizationType && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.organizationType}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Organization Name - Search for user creation, Dropdown for self-registration */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12" style={{ position: 'relative' }}>
                                            {!isUserCreation ? (
                                                // Dropdown for self-registration
                                                <>
                                                    <SearchableDropdown
                                                        choices={organizationOptions.map(org => ({
                                                            value: org.orgName,
                                                            label: `${org.orgName} ${org.stateIssuedId ? `(ID: ${org.stateIssuedId})` : ''}`
                                                        }))}
                                                        emptyChoiceLabel="Select Organization"
                                                        mandatoryLabel={true}
                                                        name="organizationName"
                                                        label="Organization Name"
                                                        value={organizationDetails.organizationName}
                                                        onChange={(event) => {
                                                            const selectedOrg = organizationOptions.find(org => org.orgName === event.target.value);
                                                            if (selectedOrg) {
                                                                handleOrganizationSelection(selectedOrg, false);
                                                            }
                                                        }}
                                                    />
                                                    {orgSearchLoading && (
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#666',
                                                            marginTop: '5px'
                                                        }}>
                                                            Loading organizations...
                                                        </div>
                                                    )}
                                                    {!orgSearchLoading && organizationOptions.length === 0 && organizationDetails.organizationType && !organizationDetails.organizationName && (
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#666',
                                                            marginTop: '5px'
                                                        }}>
                                                            No organizations found for selected type
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                // Search input for user creation
                                                <>
                                                    <InputText
                                                        label="Organization Name"
                                                        name="organizationName"
                                                        value={organizationDetails.organizationName}
                                                        type="text"
                                                        onChange={handleOrganizationInputChange}
                                                        mandatoryLabel={true}
                                                        placeholder="Start typing organization name..."
                                                    />

                                                    {/* Loading indicator */}
                                                    {orgSearchLoading && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            right: '10px',
                                                            top: '35px',
                                                            fontSize: '12px',
                                                            color: '#666'
                                                        }}>
                                                            Searching...
                                                        </div>
                                                    )}

                                                    {/* Organization dropdown */}
                                                    {isOrgDropdownOpen && organizationOptions.length > 0 && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: 'white',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            zIndex: 1000,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                        }}>
                                                            {organizationOptions.map((org) => (
                                                                <div
                                                                    key={org.orgCd}
                                                                    onClick={() => handleOrganizationSelection(org)}
                                                                    style={{
                                                                        padding: '10px',
                                                                        cursor: 'pointer',
                                                                        borderBottom: '1px solid #eee',
                                                                        backgroundColor: 'white'
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor = 'white';
                                                                    }}
                                                                >
                                                                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                                        {org.orgName}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                                        Type: {org?.orgType?.value}
                                                                        {org?.stateIssuedId && (
                                                                            <span> | ID: {org?.stateIssuedId}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {errors.organizationName && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.organizationName}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* State Issued ID */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <fieldset disabled={isUserCreation} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
                                            <InputText
                                                label="State Issued ID"
                                                name="stateIssuedId"
                                                value={organizationDetails.stateIssuedId}
                                                type="text"
                                                onChange={handleOrganizationInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter state issued ID"
                                            />
                                            </fieldset>
                                            {errors.stateIssuedId && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.stateIssuedId}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Domains */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                color: "#1b1b1b"
                                            }}>
                                                Organization Domains
                                                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                                    {isUserCreation
                                                        ? '(Optional - Add email domains associated with your organization)'
                                                        : '(Domains from selected organization)'}
                                                </span>
                                            </label>

                                            {/* Only show domain input for user creation */}
                                            {isUserCreation && (
                                                <>
                                                    {/* Domain validation error message */}
                                                    {domainError && (
                                                        <div style={{
                                                            color: "red",
                                                            fontSize: "14px",
                                                            marginBottom: "8px",
                                                            padding: "8px",
                                                            borderRadius: "4px"
                                                        }}>
                                                            {domainError}
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <input
                                                                name="domainInput"
                                                                disabled={isUserCreation}
                                                                value={domainInput}
                                                                type="text"
                                                                onChange={handleDomainInputChange}
                                                                onKeyDown={handleDomainInputKeyDown}
                                                                placeholder="nebraska.gov"
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: domainError ? '1px solid #d32f2f' : '1px solid #565c65',
                                                                    borderRadius: '4px',
                                                                    fontSize: '16px',
                                                                    marginTop: '5px'
                                                                }}
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={handleAddDomain}
                                                            disabled={isUserCreation}
                                                            style={{
                                                                marginTop: '5px',
                                                                padding: '10px 20px',
                                                                backgroundColor: '#00607f',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontWeight: 'bold',
                                                                fontSize: '14px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#004d66';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#00607f';
                                                            }}
                                                        >
                                                            Add Domain
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {/* Display added domains */}
                                            {organizationDetails.domains.length > 0 && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#f9f9f9'
                                                }}>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '10px',
                                                        color: '#00607f'
                                                    }}>
                                                        {isUserCreation ? 'Added Domains' : 'Organization Domains'} ({organizationDetails.domains.length}):
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {organizationDetails.domains.map((domain, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '6px 12px',
                                                                    backgroundColor: '#e3f2fd',
                                                                    border: '1px solid #00607f',
                                                                    borderRadius: '20px',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                <span>{domain}</span>
                                                                {/* Only show remove button in user creation mode */}
                                                                {isUserCreation && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveDomain(domain)}
                                                                        disabled={isUserCreation}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            color: '#d32f2f',
                                                                            cursor: 'pointer',
                                                                            fontSize: '16px',
                                                                            fontWeight: 'bold',
                                                                            padding: '0',
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                        onMouseOver={(e) => {
                                                                            e.currentTarget.style.color = '#b71c1c';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.currentTarget.style.color = '#d32f2f';
                                                                        }}
                                                                        title="Remove domain"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Organization Addresses */}
                                    <div className="grid-row" style={{ marginBottom: '20px', marginTop: '30px' }}>
                                        <div className="grid-col-12">
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                color: "#1b1b1b"
                                            }}>
                                                Organization Addresses
                                                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                                    {isUserCreation
                                                        ? '(Optional - Add physical addresses for your organization)'
                                                        : '(Addresses from selected organization)'}
                                                </span>
                                            </label>

                                            {/* Only show address input form for user creation */}
                                            {isUserCreation && (
                                                <>
                                                    {/* Address validation error message */}
                                                    {addressError && (
                                                        <div style={{
                                                            color: "red",
                                                            fontSize: "14px",
                                                            marginBottom: "8px",
                                                            padding: "8px",
                                                            borderRadius: "4px"
                                                        }}>
                                                            {addressError}
                                                        </div>
                                                    )}

                                                    {/* Address input form */}
                                                    <fieldset disabled={isUserCreation} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
                                                    <div style={{
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        padding: '15px',
                                                        backgroundColor: '#f9f9f9',
                                                        marginTop: '10px'
                                                    }}>
                                                        <div style={{ marginBottom: '10px' }}>
                                                            <InputText
                                                                label="Address Line 1"
                                                                name="addressLine1"
                                                                value={addressInput.addressLine1}
                                                                type="text"
                                                                onChange={handleAddressInputChange}
                                                                mandatoryLabel={false}
                                                                placeholder="Enter street address"
                                                            />
                                                        </div>

                                                        <div style={{ marginBottom: '10px' }}>
                                                            <InputText
                                                                label="Address Line 2"
                                                                name="addressLine2"
                                                                value={addressInput.addressLine2}
                                                                type="text"
                                                                onChange={handleAddressInputChange}
                                                                mandatoryLabel={false}
                                                                placeholder="Apartment, suite, unit, etc. (optional)"
                                                            />
                                                        </div>

                                                        <div style={{ marginBottom: '10px' }}>
                                                            <InputText
                                                                label="City"
                                                                name="city"
                                                                value={addressInput.city}
                                                                type="text"
                                                                onChange={handleAddressInputChange}
                                                                mandatoryLabel={false}
                                                                placeholder="Enter city"
                                                            />
                                                        </div>

                                                        <div style={{ marginBottom: '10px' }}>
                                                            <Dropdown
                                                                key={`state-${editingAddressIndex}-${addressInput.state}`}
                                                                choices={stateOptions}
                                                                emptyChoiceLabel="Select State"
                                                                mandatoryLabel={false}
                                                                name="state"
                                                                label="State"
                                                                value={addressInput.state || ''}
                                                                onChange={handleAddressInputChange}
                                                            />
                                                        </div>

                                                        <div style={{ marginBottom: '10px' }}>
                                                            <InputText
                                                                label="ZIP Code"
                                                                name="zip"
                                                                value={addressInput.zip}
                                                                type="text"
                                                                onChange={handleAddressInputChange}
                                                                mandatoryLabel={false}
                                                                placeholder="12345 or 12345-6789"
                                                            />
                                                        </div>

                                                        <div style={{ marginBottom: '10px' }}>
                                                            <Dropdown
                                                                key={`country-${editingAddressIndex}-${addressInput.country}`}
                                                                choices={countryOptions}
                                                                emptyChoiceLabel="Select Country"
                                                                mandatoryLabel={false}
                                                                name="country"
                                                                label="Country"
                                                                value={addressInput.country || ''}
                                                                onChange={handleAddressInputChange}
                                                            />
                                                        </div>

                                                        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                                            <button
                                                                type="button"
                                                                onClick={handleSaveAddress}
                                                                style={{
                                                                    padding: '10px 20px',
                                                                    backgroundColor: '#00607f',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px'
                                                                }}
                                                                onMouseOver={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#004d66';
                                                                }}
                                                                onMouseOut={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#00607f';
                                                                }}
                                                            >
                                                                {editingAddressIndex !== null ? 'Update Address' : 'Add Address'}
                                                            </button>
                                                            {editingAddressIndex !== null && (
                                                                <button
                                                                    type="button"
                                                                    onClick={handleCancelAddressEdit}
                                                                    style={{
                                                                        padding: '10px 20px',
                                                                        backgroundColor: '#6c757d',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 'bold',
                                                                        fontSize: '14px'
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        e.currentTarget.style.backgroundColor = '#5a6268';
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.currentTarget.style.backgroundColor = '#6c757d';
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    </fieldset>
                                                </>
                                            )}

                                            {/* Display added addresses */}
                                            {organizationDetails.addresses.length > 0 && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#f9f9f9'
                                                }}>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '5px',
                                                        color: '#00607f'
                                                    }}>
                                                        {isUserCreation ? 'Added Addresses' : 'Organization Addresses'} ({organizationDetails.addresses.length}):
                                                    </div>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        marginBottom: '10px',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Check a box to associate an address with the user account
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {organizationDetails.addresses.map((address, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    padding: '10px',
                                                                    backgroundColor: editingAddressIndex === index ? '#fff3cd' : (selectedOrgAddressIndex === index ? '#d4edda' : '#e3f2fd'),
                                                                    border: selectedOrgAddressIndex === index ? '2px solid #28a745' : '1px solid #00607f',
                                                                    borderRadius: '8px',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                                    {/* Checkbox for selecting organization address */}
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedOrgAddressIndex === index}
                                                                        onChange={() => {
                                                                            // Toggle selection: if already selected, deselect; otherwise select
                                                                            setSelectedOrgAddressIndex(selectedOrgAddressIndex === index ? null : index);
                                                                        }}
                                                                        style={{
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            cursor: 'pointer',
                                                                            marginTop: '2px'
                                                                        }}
                                                                        title="Select this address to associate with user"
                                                                    />
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ marginBottom: '5px' }}>
                                                                            <strong>Address {index + 1}:</strong>
                                                                            {selectedOrgAddressIndex === index && (
                                                                                <span style={{
                                                                                    marginLeft: '10px',
                                                                                    color: '#28a745',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    ✓ Selected for User
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div>{address.addressLine1}</div>
                                                                        {address.addressLine2 && <div>{address.addressLine2}</div>}
                                                                        <div>
                                                                            {address.city}, {stateOptions.find(s => s.value === address.state)?.label || address.state} {address.zip}
                                                                        </div>
                                                                        <div>
                                                                            {countryOptions.find(c => c.value === address.country)?.label || address.country}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {/* Only show edit/remove buttons in user creation mode */}
                                                                {isUserCreation && (
                                                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleEditAddress(index)}
                                                                            disabled={isUserCreation || (editingAddressIndex !== null && editingAddressIndex !== index)}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                backgroundColor: editingAddressIndex === index ? '#ffc107' : '#28a745',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: editingAddressIndex !== null && editingAddressIndex !== index ? 'not-allowed' : 'pointer',
                                                                                fontSize: '12px',
                                                                                opacity: editingAddressIndex !== null && editingAddressIndex !== index ? 0.5 : 1
                                                                            }}
                                                                        >
                                                                            {editingAddressIndex === index ? 'Editing...' : 'Edit'}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveAddress(index)}
                                                                            disabled={isUserCreation}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                backgroundColor: '#d32f2f',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px'
                                                                            }}
                                                                            onMouseOver={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#b71c1c';
                                                                            }}
                                                                            onMouseOut={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#d32f2f';
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="grid-row" style={{ marginBottom: '20px', marginTop: '45px' }}>
                                        <div className="grid-col-12">
                                            <fieldset
                                                className={"field-wrapper"}
                                                style={{
                                                    padding: "0",
                                                    margin: "0",
                                                    border: "none",
                                                    outline: "none",
                                                    width: "100%"
                                                }}
                                            >
                                                <label htmlFor="org-notes-editor" style={{
                                                    display: "block",
                                                    marginBottom: "0.5rem",
                                                    fontSize: "16px",
                                                    fontWeight: "bold",
                                                    color: "#1b1b1b"
                                                }}>
                                                    Notes:
                                                </label>
                                                <ReactQuill
                                                    className="org-notes-editor"
                                                    style={{
                                                        border: '1px solid #3e3e3f',
                                                        maxWidth: '30rem',
                                                        width: '100%',
                                                        marginTop: '9px',
                                                        overflowY: 'auto'
                                                    }}
                                                    value={organizationDetails.notes}
                                                    onChange={(value) => setOrganizationDetails(prev => ({ ...prev, notes: value }))}
                                                    modules={{
                                                        toolbar: [
                                                            ["bold", "italic", "underline", "strike"],
                                                            [{ list: "ordered" }, { list: "bullet" }],
                                                            [{ align: [] }],
                                                            ["clean"],
                                                        ],
                                                    }}
                                                />
                                            </fieldset>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-col flex-1" />
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "30px" }}>
                                <Button
                                    onClick={handleBack}
                                >
                                    Back
                                </Button>
                                <Button
                                    disabled={!isOrganizationDetailsValid()}
                                    onClick={handleOrganizationNext}
                                >
                                    Next
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Registration Form Step */}
                    {currentStep === 'registration' && (
                        <>
                            <div>
                                <p style={{ textAlign: 'center', marginBottom: '20px', fontSize: '18px', fontWeight: 'bold', color: '#00607f' }}>
                                    Selected User Type: {getUserTypeLabel()}
                                </p>
                                <p style={{ textAlign: 'center', marginBottom: '30px', fontSize: '16px', color: '#666' }}>
                                    Please fill in all the required information to register for access to Nebraska state applications.
                                </p>
                            </div>

                            {/* General Error Display - Moved to top of form */}
                            {errors.general && (
                                <div style={{
                                    color: "red",
                                    marginTop: "20px",
                                    marginBottom: "20px",
                                    padding: "10px",
                                    textAlign: "center"
                                }}>
                                    {errors.general}
                                </div>
                            )}

                            {/* Form Fields - Each in its own row */}
                            <div className="grid-row" style={{ marginBottom: '20px' }}>
                                <div className="grid-col flex-1" />
                                <div className="grid-col" style={{
                                    width: "50%",
                                    maxWidth: "600px",
                                    margin: "0 auto"
                                }}>
                                    {/* Row 1: First Name */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="First Name"
                                                name="firstName"
                                                value={formData.firstName}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter your first name"
                                            />
                                            {errors.firstName && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.firstName}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: Last Name */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Last Name"
                                                name="lastName"
                                                value={formData.lastName}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter your last name"
                                            />
                                            {errors.lastName && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.lastName}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 3: User Name with auto-generation */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <div style={{ position: 'relative' }}>
                                                <InputText
                                                    label="User Name"
                                                    name="userName"
                                                    value={formData.userName}
                                                    type="text"
                                                    onChange={handleInputChange}
                                                    mandatoryLabel={true}
                                                    placeholder="Username will be auto-generated"
                                                />

                                                {/* Username helper text and regenerate button */}
                                                <div style={{
                                                    marginTop: '5px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '10px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        flex: '1'
                                                    }}>
                                                        {formData.firstName && formData.lastName ? (
                                                            <span>
                                                                Suggested: <strong>{suggestedUsername}</strong>
                                                                {isUserNameModified && (
                                                                    <span style={{ color: '#28a745', marginLeft: '5px' }}>
                                                                        ✓ Custom username
                                                                    </span>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span>Enter first and last name to auto-generate username</span>
                                                        )}
                                                    </div>

                                                    {formData.firstName && formData.lastName && (
                                                        <button
                                                            type="button"
                                                            onClick={handleRegenerateUsername}
                                                            style={{
                                                                fontSize: '12px',
                                                                padding: '4px 8px',
                                                                backgroundColor: '#00607f',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#00607f';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#00607f';
                                                            }}
                                                        >
                                                            Use Suggested
                                                        </button>
                                                    )}
                                                </div>
                                                {errors.userName && (
                                                    <div style={{ color: "red", marginTop: "5px" }}>
                                                        {errors.userName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 4: Job Title */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Job Title"
                                                name="jobTitle"
                                                value={formData.jobTitle}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={false}
                                                placeholder="Enter your job title"
                                            />
                                            {errors.jobTitle && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.jobTitle}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 5: Email */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Email"
                                                name="email"
                                                value={formData.email}
                                                type="email"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter your email address"
                                            />
                                            {errors.email && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.email}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 5: Phone */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Phone"
                                                name="phone"
                                                value={formData.phone}
                                                type="tel"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="(XXX)-XXX-XXXX"
                                            />
                                            {errors.phone && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.phone}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Address Section Header */}
                                    <div className="grid-row" style={{ marginTop: '30px', marginBottom: '15px' }}>
                                        <div className="grid-col-12">
                                            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#00607f' }}>
                                                Address Information
                                                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                                                    (All fields except Address Line 2 are required)
                                                </span>
                                            </h4>
                                        </div>
                                    </div>

                                    {/* Dropdown to select from organization addresses - only for Trading Partners with org addresses */}
                                    {(selectedUserType === 'trading_partner' || selectedUserType === '30') &&
                                        organizationDetails.addresses.length > 0 && (
                                            <div className="grid-row" style={{ marginBottom: '20px' }}>
                                                <div className="grid-col-12">
                                                    <label
                                                        htmlFor="selectOrgAddress"
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '16px',
                                                            fontWeight: '500',
                                                            color: '#1b1b1b',
                                                            marginBottom: '8px'
                                                        }}
                                                    >
                                                        Use Organization Address
                                                    </label>
                                                    <select
                                                        id="selectOrgAddress"
                                                        value={useSameAsOrgAddress && selectedOrgAddressIndex !== null ? selectedOrgAddressIndex.toString() : ''}
                                                        onChange={(e) => {
                                                            const selectedValue = e.target.value;
                                                            if (selectedValue === '') {
                                                                // "Enter manually" selected - clear the address fields
                                                                setUseSameAsOrgAddress(false);
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    addressLine1: '',
                                                                    addressLine2: '',
                                                                    city: '',
                                                                    state: '',
                                                                    zip: '',
                                                                    country: ''
                                                                }));
                                                            } else {
                                                                // An address was selected
                                                                const index = parseInt(selectedValue, 10);
                                                                const orgAddress = organizationDetails.addresses[index];
                                                                setSelectedOrgAddressIndex(index);
                                                                setUseSameAsOrgAddress(true);
                                                                if (orgAddress) {
                                                                    const stateValue = orgAddress.state ? String(orgAddress.state).trim() : '';
                                                                    const countryValue = orgAddress.country ? String(orgAddress.country).trim() : '';
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        addressLine1: orgAddress.addressLine1 || '',
                                                                        addressLine2: orgAddress.addressLine2 || '',
                                                                        city: orgAddress.city || '',
                                                                        state: stateValue,
                                                                        zip: orgAddress.zip || '',
                                                                        country: countryValue
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            fontSize: '14px',
                                                            border: '1px solid #00607f',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f0f9ff',
                                                            cursor: 'pointer',
                                                            appearance: 'menulist'
                                                        }}
                                                    >
                                                        <option value="">-- Enter address manually --</option>
                                                        {organizationDetails.addresses.map((address, index) => (
                                                            <option key={index} value={index.toString()}>
                                                                {`${address.addressLine1}${address.addressLine2 ? ', ' + address.addressLine2 : ''}, ${address.city}, ${stateOptions.find(s => s.value === address.state)?.label || address.state} ${address.zip}, ${countryOptions.find(c => c.value === address.country)?.label || address.country}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        marginTop: '5px',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {useSameAsOrgAddress
                                                            ? 'Address fields are populated from the selected organization address'
                                                            : 'Select an organization address or enter your address manually below'}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    {/* Row 6: Address Line 1 */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Address Line 1"
                                                name="addressLine1"
                                                value={formData.addressLine1}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter street address"
                                            />

                                            {/* Address suggestion helper text and button */}
                                            {formData.addressLine1.trim() && (
                                                <div style={{
                                                    marginTop: '5px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '10px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        flex: '1'
                                                    }}>
                                                        <span>Click to validate and use suggested address</span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={handleSuggestAddress}
                                                        style={{
                                                            fontSize: '12px',
                                                            padding: '4px 8px',
                                                            backgroundColor: '#00607f',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#004d66';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#00607f';
                                                        }}
                                                    >
                                                        Use Suggested
                                                    </button>
                                                </div>
                                            )}

                                            {errors.addressLine1 && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.addressLine1}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 7: Address Line 2 */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="Address Line 2"
                                                name="addressLine2"
                                                value={formData.addressLine2}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={false}
                                                placeholder="Apartment, suite, unit, etc. (optional)"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 8: City */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="City"
                                                name="city"
                                                value={formData.city}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="Enter city"
                                            />
                                            {errors.city && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.city}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 9: State */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <Dropdown
                                                key={`user-state-${formData.state}-${useSameAsOrgAddress}`}
                                                choices={stateOptions}
                                                emptyChoiceLabel="Select State"
                                                mandatoryLabel={true}
                                                name="state"
                                                label="State"
                                                value={formData.state || ''}
                                                onChange={handleDropdownChange}
                                            />
                                            {errors.state && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.state}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 10: ZIP Code */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <InputText
                                                label="ZIP Code"
                                                name="zip"
                                                value={formData.zip}
                                                type="text"
                                                onChange={handleInputChange}
                                                mandatoryLabel={true}
                                                placeholder="12345 or 12345-6789"
                                            />
                                            {errors.zip && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.zip}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 11: Country */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <Dropdown
                                                key={`user-country-${formData.country}-${useSameAsOrgAddress}`}
                                                choices={countryOptions}
                                                emptyChoiceLabel="Select Country"
                                                mandatoryLabel={true}
                                                name="country"
                                                label="Country"
                                                value={formData.country || ''}
                                                onChange={handleDropdownChange}
                                            />
                                            {errors.country && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.country}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 12: Notes */}
                                    <div className="grid-row" style={{ marginBottom: '20px', marginTop: '45px' }}>
                                        <div className="grid-col-12">
                                            <fieldset
                                                className={"field-wrapper"}
                                                style={{
                                                    padding: "0",
                                                    margin: "0",
                                                    border: "none",
                                                    outline: "none",
                                                    width: "100%"
                                                }}
                                            >
                                                <label htmlFor="my-editor" style={{
                                                    display: "block",
                                                    marginBottom: "0.5rem",
                                                    fontSize: "16px",
                                                    fontWeight: "bold",
                                                    color: "#1b1b1b"
                                                }}>
                                                    Notes:
                                                </label>
                                                <ReactQuill
                                                    className="my-editor"
                                                    style={{
                                                        width: "100%",
                                                        maxHeight: "100px",
                                                        overflowY: "auto"
                                                    }}
                                                    value={formData.notes}
                                                    onChange={(value) => setFormData(prev => ({ ...prev, notes: value }))}
                                                    modules={{
                                                        toolbar: [
                                                            ["bold", "italic", "underline", "strike"],
                                                            [{ list: "ordered" }, { list: "bullet" }],
                                                            [{ align: [] }],
                                                            ["clean"],
                                                        ],
                                                    }}
                                                />
                                            </fieldset>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-col flex-1" />
                            </div>


                            {/* Action Buttons */}
                            <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "30px" }}>
                                <Button
                                    onClick={handleBack}
                                >
                                    Previous
                                </Button>
                                <Button
                                    disabled={!isFormValid()}
                                    onClick={handleSubmit}
                                >
                                    Submit Registration
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <FooterData
                styles={false}
            />
        </>
    );
};

export default SelfRegistration;
